/**
 * useFrecency — localStorage-backed frecency scoring for file ranking
 *
 * Tracks file access frequency + recency to rank autocomplete results.
 * Score: frequency * (1 / (1 + daysSinceLastAccess))
 * All data in refs — no rerenders on access tracking.
 */
import { useRef, useCallback } from 'react'

const STORAGE_PREFIX = 'papan-agent-frecency-'
const MAX_ENTRIES = 500

interface FrecencyEntry {
  path: string
  frequency: number
  lastAccess: number // epoch ms
}

function load(workspaceId: string): FrecencyEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + workspaceId)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (e: any): e is FrecencyEntry =>
        typeof e?.path === 'string' &&
        typeof e?.frequency === 'number' &&
        typeof e?.lastAccess === 'number',
    )
  } catch {
    return []
  }
}

function save(workspaceId: string, entries: FrecencyEntry[]): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + workspaceId, JSON.stringify(entries))
  } catch { /* ignore quota errors */ }
}

function computeScore(entry: FrecencyEntry): number {
  const daysSince = (Date.now() - entry.lastAccess) / (1000 * 60 * 60 * 24)
  return entry.frequency * (1 / (1 + daysSince))
}

export function useFrecency(workspaceId: string) {
  const entriesRef = useRef<FrecencyEntry[]>(load(workspaceId))

  const getScore = useCallback((path: string): number => {
    const entry = entriesRef.current.find(e => e.path === path)
    if (!entry) return 0
    return computeScore(entry)
  }, [])

  const recordAccess = useCallback((path: string) => {
    const entries = entriesRef.current
    const idx = entries.findIndex(e => e.path === path)

    if (idx >= 0) {
      entries[idx].frequency += 1
      entries[idx].lastAccess = Date.now()
    } else {
      entries.push({ path, frequency: 1, lastAccess: Date.now() })
    }

    // Evict lowest-score entries when over limit
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => computeScore(b) - computeScore(a))
      entries.length = MAX_ENTRIES
    }

    entriesRef.current = entries
    save(workspaceId, entries)
  }, [workspaceId])

  const sortByFrecency = useCallback((paths: string[]): string[] => {
    const entries = entriesRef.current
    const scoreMap = new Map<string, number>()
    for (const entry of entries) {
      scoreMap.set(entry.path, computeScore(entry))
    }

    return [...paths].sort((a, b) => {
      const sa = scoreMap.get(a) ?? 0
      const sb = scoreMap.get(b) ?? 0
      return sb - sa
    })
  }, [])

  return { getScore, recordAccess, sortByFrecency }
}
